import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationApi } from '../../api/endpoints.js';
import { useNotice } from '../../state/NoticeContext.jsx';
import { Mark } from '../../components/Icons.jsx';
import { Button, Card, Empty, Grid2, Modal, Muted, Spinner, Steps, Tag, fmtDate, useAsync } from '../../ui.jsx';

const statusTone = {
  draft: 'mid',
  submitted: '',
  under_review: '',
  offered: 'ok',
  rejected: 'no',
  withdrawn: ''
};

function Offer({ offer, onRespond }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState('');
  const [letter, setLetter] = useState(null);

  const respond = async (decision) => {
    setBusy(decision);
    try {
      await onRespond(offer.id, decision);
      setConfirming(false);
    } finally {
      setBusy('');
    }
  };

  const viewLetter = async () => {
    setBusy('letter');
    try {
      const data = await applicationApi.letter(offer.id);
      setLetter(data);
    } finally {
      setBusy('');
    }
  };

  if (offer.status === 'rejected' || offer.status === 'withdrawn') return null;

  return (
    <Card>
      <div className="row sp">
        <div>
          <h3 style={{ fontSize: 18, margin: 0 }}>{offer.school?.name}</h3>
          <Muted>{offer.programme}{offer.mode ? ` · ${offer.mode}` : ''}</Muted>
        </div>
        <Tag tone={offer.status === 'accepted' ? 'ok' : offer.status === 'declined' ? 'no' : 'mid'}>
          {offer.status}
        </Tag>
      </div>

      {offer.status === 'released' ? (
        <div className="note">Released because you accepted another offer.</div>
      ) : null}
      {offer.status === 'declined' ? <div className="note">You declined this offer.</div> : null}
      {offer.status === 'accepted' ? (
        <>
          <div className="note">
            <b>You accepted this offer.</b> Your admission letter is ready.
          </div>
          <Button busy={busy === 'letter'} onClick={viewLetter}>
            {letter ? 'Refresh admission letter' : 'View admission letter'}
          </Button>
        </>
      ) : null}
      {offer.status === 'expired' ? (
        <div className="note">
          This offer expired on {fmtDate(offer.expiresAt)}. Contact the school if you still want a
          place.
        </div>
      ) : null}
      {offer.status === 'open' ? (
        <>
          <div className="note">
            <b>Congratulations.</b> {offer.school?.shortName} offers you admission. Respond by{' '}
            {fmtDate(offer.expiresAt)} ({offer.daysLeft} {offer.daysLeft === 1 ? 'day' : 'days'} left).
          </div>
          {confirming ? (
            <Modal
              title="Accept this offer?"
              labelledBy={`accept-${offer.id}`}
              onClose={() => setConfirming(false)}
              footer={
                <>
                  <Button busy={busy === 'accept'} onClick={() => respond('accept')}>Yes, accept</Button>
                  <Button variant="s" onClick={() => setConfirming(false)}>Cancel</Button>
                </>
              }
            >
              {offer.school?.shortName} is offering you {offer.programme}
              {offer.mode ? ` (${offer.mode})` : ''}. Accepting makes it your firm choice and Entrance
              releases every other offer you are holding. This cannot be undone.
            </Modal>
          ) : (
            <div className="row">
              <Button onClick={() => setConfirming(true)}>Accept offer</Button>
              <Button variant="s" busy={busy === 'decline'} onClick={() => respond('decline')}>
                Decline
              </Button>
            </div>
          )}
        </>
      ) : null}

      {letter ? <Letter data={letter} /> : null}
    </Card>
  );
}

/**
 * The admission letter.
 *
 * This is a document, not a settings list, so it is laid out on the `.letter`
 * and `.lt` styles the design already had but nothing used. The old version was
 * a plain `dl`, which is why it read as a debug dump rather than a letter.
 */
function Letter({ data }) {
  const letter = data.letter || data;
  // `school` arrives as a populated document `{ shortName, name }`, not a string.
  // Rendering it directly threw "Objects are not valid as a React child" and took
  // the whole page down. Accept either shape.
  const school = typeof letter.school === 'string' ? null : letter.school;
  const schoolName =
    typeof letter.school === 'string' ? letter.school : school ? school.shortName || school.name : '';
  const schoolFull = school ? school.name : '';
  // The API names the recipient `candidate`; the old component looked for
  // `student`, so that row silently never rendered.
  const candidateName = letter.student || letter.candidate?.name || '';
  const issued = fmtDate(letter.issuedAt);

  return (
    <>
      <div className="noprint" style={{ marginTop: 14 }}>
        <div className="row sp">
          <Button size="sm" onClick={() => window.print()}>Print or save as PDF</Button>
          <Muted>Only this letter prints, not the rest of the page.</Muted>
        </div>
      </div>

      <article className="letter" style={{ marginTop: 14 }}>
        <header style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <span style={{ color: '#0a5f93', flex: 'none' }}>
            <Mark />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 21, margin: 0 }}>{schoolFull || schoolName}</h1>
            <div className="mu">{schoolName && schoolFull ? schoolName : ''}</div>
            <div className="mu">Admissions Office</div>
          </div>
          <div style={{ textAlign: 'right', flex: 'none' }} className="mu">
            <div>Ref. {letter.reference || letter.letterReference || 'Not set'}</div>
            <div>Issued {issued}</div>
          </div>
        </header>

        <hr />

        <h1 style={{ fontSize: 17, margin: '0 0 4px' }}>Admission offer</h1>
        <p className="mu" style={{ margin: '0 0 16px' }}>
          {issued ? `${issued} · ` : ''}Dear {candidateName || 'applicant'},
        </p>

        <p style={{ margin: '0 0 14px', lineHeight: 1.6 }}>
          We are pleased to inform you that your application to{' '}
          <b>{schoolFull || schoolName || 'this institution'}</b> has been successful. You have been
          offered a place on the programme below. This offer is subject to the conditions set out at
          the end of this letter.
        </p>

        <table className="lt">
          <tbody>
            <tr><td>Candidate</td><td><b>{candidateName || 'Not set'}</b></td></tr>
            {letter.candidate?.indexNumber ? (
              <tr><td>Index number</td><td>{letter.candidate.indexNumber}</td></tr>
            ) : null}
            {letter.candidate?.aggregate ? (
              <tr><td>Aggregate</td><td>{letter.candidate.aggregate}</td></tr>
            ) : null}
            <tr><td>Programme</td><td><b>{letter.programme || 'Not set'}</b></td></tr>
            <tr><td>Mode of study</td><td>{letter.mode || 'Not stated'}</td></tr>
            {letter.hall ? <tr><td>Hall</td><td>{letter.hall}</td></tr> : null}
            <tr><td>Fees</td><td>{letter.tuition || 'As published by the school'}</td></tr>
            <tr><td>Reporting date</td><td>{letter.reportingDate || 'To be confirmed'}</td></tr>
            <tr><td>Verification code</td><td>{letter.verificationCode || 'Not set'}</td></tr>
          </tbody>
        </table>

        {(letter.conditions || []).length ? (
          <>
            <h3 className="hd">Conditions of admission</h3>
            <ol style={{ margin: '0 0 14px', paddingLeft: 20, lineHeight: 1.6 }}>
              {letter.conditions.map((c, i) => <li key={i}>{c}</li>)}
            </ol>
          </>
        ) : null}

        <p style={{ margin: '0 0 4px' }}>
          We look forward to welcoming you. Quote your reference above in any correspondence.
        </p>

        <div style={{ marginTop: 34, display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <div>
            <div style={{ borderTop: '1px solid #14232e', paddingTop: 6, minWidth: 210 }}>
              <b>Admissions Officer</b>
            </div>
            <div className="mu">For {schoolFull || schoolName || 'the school'}</div>
          </div>
          <div style={{ textAlign: 'right' }} className="mu">
            <div>Issued via Entrance</div>
            <div>{issued}</div>
          </div>
        </div>
      </article>
    </>
  );
}


export default function Track() {
  const notice = useNotice();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => applicationApi.list(), []);
  const [open, setOpen] = useState(null);

  if (loading) return <Spinner label="Loading your applications" />;
  if (error) return <div className="note">We could not load your applications. Please try again.</div>;

  const applications = data?.applications || [];
  const offers = data?.offers || [];
  const liveOffers = offers.filter((o) => o.status !== 'released');
  const active = open ? applications.find((a) => a.id === open) : null;

  const respond = async (offerId, decision) => {
    try {
      const result = await applicationApi.respondToOffer(offerId, { decision });
      notice.success(result.message);
      reload();
    } catch (err) {
      notice.fromError(err);
    }
  };

  if (!applications.length) {
    return (
      <Empty title="Nothing to track yet" action={<Button onClick={() => navigate('/apply')}>Start an application</Button>}>
        Once you apply to a school, its progress and any offers appear here.
      </Empty>
    );
  }

  return (
    <>
      <h2>Track</h2>
      <Muted>Every school you have applied to, in one place.</Muted>

      {liveOffers.length ? (
        <>
          <h3 style={{ margin: '22px 0 10px' }}>Offers</h3>
          {liveOffers.map((offer) => (
            <Offer key={offer.id} offer={offer} onRespond={respond} />
          ))}
        </>
      ) : null}

      <h3 style={{ margin: '22px 0 10px' }}>Applications</h3>
      {applications.map((item) => (
        <Card key={item.id}>
          <div className="row sp">
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <i className="ub">{item.school?.shortName}</i>
              <div>
                <b>{item.school?.name}</b>
                <div className="mu">
                  {item.reference} · {item.programmes.map((p) => p.name).join(', ') || 'No programme chosen'}
                </div>
              </div>
            </div>
            <Tag tone={statusTone[item.status]}>{item.status.replace(/_/g, ' ')}</Tag>
          </div>

          <div style={{ margin: '12px 0' }}>
            <Steps
              current={item.progress}
              items={(item.timeline || []).map((t) => t.stage)}
            />
          </div>

          {item.decisionNote ? <div className="note">{item.decisionNote}</div> : null}
          {item.awaitingResults ? (
            <div className="note">
              You told {item.school?.shortName} you are awaiting results, so your application can be
              considered once they are released.
            </div>
          ) : null}

          {item.status === 'draft' ? (
            <Button onClick={() => navigate('/apply')}>Continue application</Button>
          ) : (
            <Button
              variant="s"
              size="sm"
              onClick={() => setOpen(open === item.id ? null : item.id)}
            >
              {open === item.id ? 'Hide history' : 'Show history'}
            </Button>
          )}

          {open === item.id ? (
            <div style={{ marginTop: 12 }}>
              {(item.history || []).map((entry, i) => (
                <div className="prog" key={`${entry.at}-${i}`}>
                  <div style={{ flex: 1 }}>
                    <b>{String(entry.to || '').replace(/_/g, ' ')}</b>
                    {entry.note ? <div className="mu">{entry.note}</div> : null}
                  </div>
                  <span className="mu">{fmtDate(entry.at)}</span>
                </div>
              ))}
              {item.status === 'submitted' || item.status === 'under_review' ? (
                <WithdrawButton id={item.id} onDone={reload} />
              ) : null}
            </div>
          ) : null}
        </Card>
      ))}
    </>
  );
}

function WithdrawButton({ id, onDone }) {
  const notice = useNotice();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const withdraw = async () => {
    setBusy(true);
    try {
      const data = await applicationApi.withdraw(id, reason.trim() || undefined);
      notice.success(data.message || 'Application withdrawn.');
      onDone();
    } catch (error) {
      notice.fromError(error);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div style={{ marginTop: 10 }}>
        <Button variant="s" size="sm" onClick={() => setOpen(true)}>Withdraw this application</Button>
      </div>
    );
  }

  return (
    <Modal
      title="Withdraw this application?"
      labelledBy={`withdraw-${id}`}
      onClose={() => setOpen(false)}
      footer={
        <>
          <Button size="sm" variant="s" busy={busy} onClick={withdraw}>
            Confirm withdrawal
          </Button>
          <Button size="sm" variant="s" onClick={() => setOpen(false)}>Keep it</Button>
        </>
      }
    >
      <div style={{ marginBottom: 8 }}>
        The school will see that you withdrew. Your master profile is not affected, and the
        application fee is not refunded.
      </div>
      <input
        data-autofocus
        placeholder="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}
